import { fetchKlaviyoSubscribe } from "@repo/shopify/api/commonApi";
import { validateEmail } from "@repo/shopify/utils/regex";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Button from "@atoms/Button";
import Input from "@atoms/Input";

import { NOTIFICATIONS } from "@const/notifications";

import useLanguage from "@hooks/useLanguage";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

interface IFormData {
  newsletter: string;
  acceptsMarketing: boolean;
}

export default function NewsLetterInput() {
  const { t } = useTranslation();

  const { openToast, openModal } = useLayoutStore();

  const { customer } = useCustomerStore();

  const { lang } = useLanguage();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm<IFormData>({
    defaultValues: { newsletter: customer?.email || "", acceptsMarketing: customer?.acceptsMarketing || false },
  });

  const { acceptsMarketing } = watch();

  const onSubmit = handleSubmit(async (formData) => {
    const publicKey = lang === "ko" ? "X87E9K" : "TmYXu3";
    const listId = lang === "ko" ? "WTpimJ" : "W9HZfc";
    const response = await fetchKlaviyoSubscribe(formData.newsletter, publicKey, listId);
    if (response) {
      openToast(NOTIFICATIONS.SUCCESS_NEWSLETTER_SUBSCRIBE);
      reset();
      openModal(null);
    }
  });

  const displayError = (key: string) => {
    return { isError: Object.keys(errors).includes(key), errorMessage: errors[key as keyof typeof errors]?.message };
  };

  return (
    <form className="flex flex-col gap-2.5" onSubmit={onSubmit}>
      <Input
        {...register("newsletter", {
          required: { value: true, message: t("error.necessary_email") },
          validate(value) {
            if (!validateEmail(value)) return t("login.invalid_email");
          },
        })}
        {...displayError("newsletter")}
        placeholder="Email Address"
        type="email"
      />
      <Input
        {...register("acceptsMarketing", { required: t("error.necessary_consent") })}
        {...displayError("acceptsMarketing")}
        checked={acceptsMarketing}
        id="acceptsMarketing"
        label={t("news_letter.accepts_marketing")}
        onChange={() => {
          clearErrors("acceptsMarketing");
          setValue("acceptsMarketing", !acceptsMarketing);
        }}
        type="checkbox"
      />
      <Button as="square" type="submit">
        Subscribe
      </Button>
    </form>
  );
}
